import { CounterInput, Text, Flex } from '@buffer-mono/popcorn'
import cx from 'clsx'
import { useState, useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { CHANNEL_LIMITS } from '../../constants'
import type { ExtendedPlanOption } from '../../utils/dataTransformers'
import { SavingsHint } from '../SavingsHint'

import styles from './ChannelSelector.module.css'

interface ChannelSelectorProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  planPricing?: ExtendedPlanOption['pricing']
}

interface DiscountProximity {
  isOneStep: boolean
  nextThreshold?: number
}

/**
 * Checks if adding one more channel would unlock a volume discount
 * @param currentChannels - The current number of channels
 * @param planPricing - The pricing tiers for the plan
 * @returns Object indicating if user is one step from discount and the next threshold
 */
function checkDiscountProximity(
  currentChannels: number,
  planPricing?: ExtendedPlanOption['pricing'],
): DiscountProximity {
  if (!planPricing) {
    return { isOneStep: false }
  }

  const sortedTiers = planPricing.toSorted(
    (a, b) => a.upperBound - b.upperBound,
  )

  // Find current tier
  const currentTier = sortedTiers.find(
    (tier) => currentChannels <= tier.upperBound,
  )

  if (!currentTier) {
    return { isOneStep: false }
  }

  // Check if at boundary and there's a next tier
  const isAtBoundary = currentChannels === currentTier.upperBound
  const currentTierIndex = sortedTiers.indexOf(currentTier)
  const hasNextTier = currentTierIndex < sortedTiers.length - 1

  return {
    isOneStep: isAtBoundary && hasNextTier,
    nextThreshold: currentTier.upperBound + 1,
  }
}

/**
 * Sorts pricing tiers by upper bound in ascending order
 */
function getSortedTiers(
  planPricing: ExtendedPlanOption['pricing'],
): ExtendedPlanOption['pricing'] {
  return planPricing.toSorted((a, b) => a.upperBound - b.upperBound)
}

/**
 * Custom hook to detect when user has just crossed a discount tier boundary
 * Returns true only for the render where the crossing happened
 */
function useDiscountConfirmation(
  value: number,
  planPricing?: ExtendedPlanOption['pricing'],
): boolean {
  const previousValueRef = useRef(value)
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    const previousValue = previousValueRef.current
    previousValueRef.current = value

    // Only check when value increases and pricing exists
    if (!planPricing || previousValue >= value) {
      setShowConfirmation(false)
      return
    }

    const sortedTiers = getSortedTiers(planPricing)

    // Check if we just crossed a tier boundary
    const justCrossedBoundary = sortedTiers.some((tier, index) => {
      const crossedTier =
        previousValue <= tier.upperBound && value > tier.upperBound
      const hasNextTier = index < sortedTiers.length - 1

      return crossedTier && hasNextTier
    })

    setShowConfirmation(justCrossedBoundary)
  }, [value, planPricing])

  return showConfirmation
}

/**
 * A selector component that allows users to choose the number of social media channels.
 *
 * Features:
 * - Counter input with increment/decrement buttons
 * - Singular/plural text display
 * - Dynamic width based on number of digits
 * - Volume discount hints and confirmations
 * - Auto-hiding messages with timers
 *
 * @param value - Current selected channel count
 * @param onChange - Callback function when channel count changes
 * @param min - Minimum allowed channel count (defaults to CHANNEL_LIMITS.min)
 * @param max - Maximum allowed channel count
 * @param disabled - Whether the selector is disabled
 * @param planPricing - Pricing tiers for volume discounts
 * @param className - Additional CSS class names
 * @param props - Additional props to pass to the container
 */
const ChannelSelector = ({
  value,
  onChange,
  min = CHANNEL_LIMITS.min,
  max,
  disabled = false,
  planPricing,
  className,
  ...props
}: ChannelSelectorProps): JSX.Element => {
  const [hintVisible, setHintVisible] = useState(false)
  // Ensure value is always valid
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : min

  // Check proximity to discount threshold
  const { isOneStep, nextThreshold } = checkDiscountProximity(
    safeValue,
    planPricing,
  )

  // Manage discount messages
  const showDiscountConfirmation = useDiscountConfirmation(
    safeValue,
    planPricing,
  )

  const showSpecialPriceHint = Boolean(isOneStep && nextThreshold)

  // Ensure onChange receives valid numbers
  const handleChange = (newValue: number): void => {
    const safeNewValue =
      typeof newValue === 'number' && !isNaN(newValue) ? newValue : min
    onChange(safeNewValue)
  }

  // Determine hint visibility and message
  useEffect(() => {
    // Recalculate visibility whenever the value changes
    const shouldShowHint = showDiscountConfirmation || showSpecialPriceHint
    setHintVisible(shouldShowHint)
  }, [safeValue, showDiscountConfirmation, showSpecialPriceHint])

  const hintMessage = showDiscountConfirmation
    ? '🎉 Discount added!'
    : `💸 Special price for ${nextThreshold}+ channels`

  const channelText = safeValue === 1 ? 'channel' : 'channels'

  return (
    <Flex
      align="center"
      gap="sm"
      justify="center"
      className={cx(styles.channelSelectorContainer, className)}
      {...props}
    >
      <div className={styles.inputWrapper}>
        <CounterInput
          value={safeValue}
          onChange={handleChange}
          name="channelCount"
          min={min}
          max={max}
          disabled={disabled}
          size="medium"
          aria-label="Number of channels"
          step={1}
          className={styles.channelSelector}
          style={
            {
              '--input-width': `${safeValue.toString().length}ch`,
            } as React.CSSProperties
          }
          suffix={<Text size="md">{channelText}</Text>}
        />
        <SavingsHint
          visible={hintVisible}
          style={{ '--arrow-position': '8px' } as React.CSSProperties}
        >
          {hintMessage}
        </SavingsHint>
      </div>
    </Flex>
  )
}

export default ChannelSelector
